import {
  FormControl,
  FormHelperText,
  InputAdornment,
  InputBaseComponentProps,
  InputLabel,
  OutlinedInput,
  Typography,
} from "@mui/material"
import { SxProps, Theme } from "@mui/material/styles"
import { Control, Controller, UseFormClearErrors } from "react-hook-form"
import React, { useState } from "react"
import {
  labelSx,
  outlineSx,
  TextFieldRequiredOverrides,
  TextFieldRequiredScheme,
} from "./TextFieldRequiredStyle"

type Size = "small" | "medium"
type Variant = "default" | "labeled" | "rounded"

export interface ValidationTextFieldProps {
  control: Control<any, any>
  fieldName: string
  label: string
  placeholder?: string
  inputProps?: InputBaseComponentProps
  required?: boolean
  startAdornment?: React.ReactNode
  endAdornment?: React.ReactNode
  backgroundColorTitle?: string
  shrink?: boolean
  scheme?: TextFieldRequiredScheme
  overrides?: TextFieldRequiredOverrides
  onKeyDown?: React.KeyboardEventHandler<
    HTMLTextAreaElement | HTMLInputElement | HTMLDivElement
  >
  onFieldExit?: () => void
  validate?: (value?: any) => boolean | string
  dataCyErrorLabel?: string
  size?: Size
  variant?: Variant
  onChangeValue?: (value: string) => void
  borderRadius?: number | string
  /** @deprecated Utilizado apenas para o número de telefone, agora está fixo por causa das validações. */
  sxInputLabel?: SxProps<Theme>
  /** @deprecated Use validação do RHF em `rules`/`trigger`. */
  clearErrors?: UseFormClearErrors<any>
  /** @deprecated Retorne mensagem em `rules.validate`. */
  validateLabel?: string
}

export default function TextFieldRequired({
  control,
  fieldName,
  label,
  placeholder,
  inputProps,
  required = true,
  startAdornment,
  endAdornment,
  backgroundColorTitle,
  shrink,
  scheme,
  overrides,
  onKeyDown,
  onFieldExit,
  validate,
  dataCyErrorLabel,
  size = "medium",
  variant = "default",
  onChangeValue,
  borderRadius,
  validateLabel,
}: ValidationTextFieldProps) {
  const [focused, setFocused] = useState(false)

  const labeled = variant === "labeled"
  const rounded = variant === "rounded"

  const rhfValidate = (v: unknown) => {
    if (!validate) return true
    const res = validate(v)
    if (typeof res === "string") return res
    return res || validateLabel || `${label} inválido`
  }

  return (
    <Controller
      control={control}
      name={fieldName}
      rules={{
        required: required ? `Campo "${label}" obrigatório` : false,
        validate: rhfValidate,
      }}
      render={({ field, fieldState: { error, isValidating } }) => {
        const errored = Boolean(error)
        const val = (field.value ?? "") as string
        const filled = val !== ""
        const shouldShrink = labeled ? true : focused || filled

        const placeholderValue = labeled
          ? placeholder ?? ""
          : filled || focused
          ? placeholder ?? ""
          : ""

        const helperId = `${fieldName}-helper`

        return (
          <FormControl fullWidth size={size} variant="outlined" error={errored}>
            {variant === "labeled" ? (
              <Typography
                gutterBottom
                fontSize="14px"
                fontWeight={500}
                lineHeight="20px"
                color="#0F172A"
                sx={labelSx({
                  labeled,
                  shouldShrink,
                  backgroundColorTitle,
                  startAdornment,
                  filled,
                  focused,
                  errored,
                  valid: isValidating,
                  scheme,
                })}
              >
                {label}
              </Typography>
            ) : (
              <InputLabel
                htmlFor={fieldName}
                shrink={shouldShrink ? true : shrink}
                sx={labelSx({
                  labeled,
                  shouldShrink,
                  backgroundColorTitle,
                  startAdornment,
                  filled,
                  focused,
                  errored,
                  valid: isValidating,
                  scheme,
                })}
              >
                {label}
              </InputLabel>
            )}
            <OutlinedInput
              fullWidth
              id={fieldName}
              notched={shouldShrink}
              value={val}
              onChange={(e) => {
                const next = e.target.value
                field.onChange(e)
                onChangeValue?.(next)
              }}
              onBlur={() => {
                setFocused(false)
                field.onBlur()
                onFieldExit?.()
              }}
              onKeyDown={onKeyDown}
              inputRef={field.ref}
              inputProps={inputProps}
              onFocus={() => setFocused(true)}
              placeholder={placeholderValue}
              startAdornment={
                startAdornment ? (
                  <InputAdornment position="start">
                    {startAdornment}
                  </InputAdornment>
                ) : undefined
              }
              endAdornment={
                endAdornment ? (
                  <InputAdornment position="end">{endAdornment}</InputAdornment>
                ) : undefined
              }
              aria-invalid={errored}
              aria-describedby={helperId}
              sx={outlineSx({
                errored,
                filled,
                valid: isValidating,
                rounded,
                borderRadius,
                scheme,
                overrides,
              })}
            />

            <FormHelperText
              id={helperId}
              error={errored}
              data-cy={dataCyErrorLabel}
              data-testid={dataCyErrorLabel}
              sx={{ mt: 0.6, fontSize: 14, fontWeight: 500 }}
            >
              {errored ? error?.message : ""}
            </FormHelperText>
          </FormControl>
        )
      }}
    />
  )
}
